import React, { useState, useEffect } from 'react';
import { Building2, MapPin, Users, ArrowRight, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../lib/utils';
import { API_BASE_URL } from '../config';

function ExpositionsView({ onTeleportToRoom, currentShowroomId, onShowConfirmation, backgroundLoadingProgress, roomReadiness }) {
  const { t } = useTranslation();
  const [showrooms, setShowrooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchEnabledShowrooms();
  }, []);

  const fetchEnabledShowrooms = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/showrooms/enabled`);
      if (!response.ok) {
        // Fallback to regular showrooms endpoint if enabled endpoint doesn't exist yet
        const fallbackResponse = await fetch(`${API_BASE_URL}/showrooms`);
        const allShowrooms = await fallbackResponse.json();
        // Filter enabled ones on client side for now
        setShowrooms(allShowrooms.filter(room => room.enabled !== false));
      } else {
        const enabledShowrooms = await response.json();
        setShowrooms(enabledShowrooms);
      }
    } catch (err) {
      console.error('Failed to fetch showrooms:', err);
      setError(t('loadingFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleTeleportClick = (showroomId) => {
    if (showroomId === currentShowroomId) {
      // Same room, no confirmation needed
      onTeleportToRoom(showroomId);
    } else {
      // Different room, show confirmation
      const currentRoom = showrooms.find(r => r.id === currentShowroomId);
      const targetRoom = showrooms.find(r => r.id === showroomId);
      onShowConfirmation(
        t('switchToRoom'),
        `${t('currentlyInRoom')} "${currentRoom?.name}". ${t('switchToRoomMessage')}`,
        () => onTeleportToRoom(showroomId)
      );
    }
  };

  const getRoomStatus = (roomId) => {
    if (!roomReadiness || !roomReadiness.has(roomId)) {
      return { ready: false, loading: false, error: false };
    }
    
    const state = roomReadiness.get(roomId);
    return {
      ready: state.loaded && !state.loading && !state.error,
      loading: state.loading,
      error: state.error
    };
  };

  const getLoadingPercentage = (roomId) => {
    // Since we don't have per-room loading percentage, 
    // we'll show overall progress for loading rooms
    if (backgroundLoadingProgress && backgroundLoadingProgress.total > 0) {
      const progress = (backgroundLoadingProgress.loaded / backgroundLoadingProgress.total) * 100;
      return Math.round(progress);
    }
    return 0;
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-gallery-400 mb-4" />
        <p className="text-gallery-500 font-medium">{t('loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-8">
        <div className="p-4 rounded-full bg-accent-50 border border-accent-200 mb-6">
          <Building2 className="w-8 h-8 text-accent-600" />
        </div>
        <h3 className="font-serif text-xl font-semibold text-gallery-900 mb-3">
          {t('error')}
        </h3>
        <p className="text-gallery-600 text-center mb-6 max-w-md">
          {error}
        </p>
        <button 
          onClick={fetchEnabledShowrooms}
          className={cn(
            "px-6 py-3 bg-accent-600 text-white font-medium rounded-lg",
            "hover:bg-accent-700 transition-colors duration-200",
            "border border-accent-600 hover:border-accent-700"
          )}
        >
          {t('tryAgain')}
        </button>
      </div>
    );
  }

  if (showrooms.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-8">
        <div className="p-4 rounded-full bg-gallery-100 border border-gallery-200 mb-6">
          <Building2 className="w-8 h-8 text-gallery-400" />
        </div>
        <h3 className="font-serif text-xl font-semibold text-gallery-900 mb-3">
          {t('allExpositions')} - {t('noEventsAvailable')}
        </h3>
        <p className="text-gallery-600 text-center max-w-md">
          {t('noEventsAvailable')}
        </p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h3 className="text-xl font-medium text-gray-900 mb-2">{t('allExpositions')}</h3>
        <p className="text-base text-gray-600">Выберите выставку для посещения</p>
      </div>

      <div className="space-y-3">
        {showrooms.map((showroom) => {
          const isCurrentRoom = showroom.id === currentShowroomId;
          const roomStatus = getRoomStatus(showroom.id);
          const loadingPercentage = getLoadingPercentage(showroom.id);
          
          return (
            <div
              key={showroom.id}
              onClick={roomStatus.ready || isCurrentRoom ? () => handleTeleportClick(showroom.id) : undefined}
              className={cn(
                "p-4 border rounded transition-colors",
                isCurrentRoom 
                  ? "border-gray-400 bg-gray-50" 
                  : roomStatus.ready 
                    ? "border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer" 
                    : "border-gray-200 bg-gray-50 cursor-not-allowed opacity-60"
              )}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">
                    {showroom.name}
                    {isCurrentRoom && (
                      <span className="ml-2 text-sm text-gray-500">({t('yourCurrentLocation')})</span>
                    )}
                  </h4>
                  <p className="text-base text-gray-600">
                    {showroom.width} × {showroom.depth}м · Потолок {showroom.height}м
                  </p>
                </div>
                
                {!isCurrentRoom && (
                  <div className="text-base">
                    {roomStatus.ready ? (
                      <button className="text-gray-900 hover:text-black font-medium">
                        Посетить →
                      </button>
                    ) : roomStatus.error ? (
                      <span className="text-red-600">
                        Ошибка
                      </span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <div className="w-12 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-blue-500 transition-all duration-300"
                            style={{ width: `${loadingPercentage}%` }}
                          />
                        </div>
                        <span className="text-gray-600 text-sm">
                          {loadingPercentage}%
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ExpositionsView;